import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { hasMeasurementConsent } from './measurementConsent'

type RuntimeWindow = Window & {
  __RUNTIME_CONFIG__?: Record<string, unknown>
}

const PRODUCTION_HOSTNAME = 'console.metrly.com'

const PUBLIC_STATIC_PATHS = new Set([
  '/register',
  '/verify-email',
  '/login',
  '/auth/login',
  '/forgot-password',
  '/auth/password',
])

function configuredValue(runtimeKey: string, viteKey: string): string {
  if (typeof window !== 'undefined') {
    const runtimeValue = (window as RuntimeWindow).__RUNTIME_CONFIG__?.[runtimeKey]
    if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== '') {
      return String(runtimeValue)
    }
  }

  const viteValue = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env?.[viteKey]
  return viteValue === undefined || viteValue === null ? '' : String(viteValue)
}

export function getGoogleAnalyticsId(): string {
  return configuredValue('GOOGLE_ANALYTICS_ID', 'VITE_GOOGLE_ANALYTICS_ID')
}

export function isGoogleAnalyticsEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (!hasMeasurementConsent()) return false
  if (window.location.hostname !== PRODUCTION_HOSTNAME) return false
  if (configuredValue('GOOGLE_ANALYTICS_ENABLED', 'VITE_GOOGLE_ANALYTICS_ENABLED') !== 'true') {
    return false
  }
  return /^G-[A-Z0-9]+$/.test(getGoogleAnalyticsId())
}

/**
 * Returns the only console paths that GA4 may receive. Secret-bearing public
 * routes are reduced to their React Router templates, and every authenticated
 * route is excluded.
 */
export function sanitizedPublicAnalyticsPath(pathname: string): string | null {
  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  if (PUBLIC_STATIC_PATHS.has(normalizedPath)) return normalizedPath
  if (/^\/auth\/password\/reset\/[^/]+$/.test(normalizedPath)) {
    return '/auth/password/reset/:token'
  }
  if (/^\/setup\/[^/]+$/.test(normalizedPath)) return '/setup/:token'
  return null
}

function referrerOrigin(): string {
  if (typeof document === 'undefined' || !document.referrer) return ''
  try {
    return new URL(document.referrer).origin
  } catch {
    return ''
  }
}

export function reportGoogleAnalyticsPageView(pathname: string): void {
  if (typeof window === 'undefined' || !isGoogleAnalyticsEnabled()) return

  const pagePath = sanitizedPublicAnalyticsPath(pathname)
  if (!pagePath || typeof window.gtag !== 'function') return

  const eventParameters: Record<string, string> = {
    send_to: getGoogleAnalyticsId(),
    page_location: `${window.location.origin}${pagePath}`,
    page_path: pagePath,
    page_title: 'Metrly Console',
  }
  const pageReferrer = referrerOrigin()
  if (pageReferrer) eventParameters.page_referrer = pageReferrer

  window.gtag('event', 'page_view', eventParameters)
}

/** Tracks only the allowlisted public routes above; query/hash-only changes are ignored. */
export function GoogleAnalyticsPageViews(): null {
  const { pathname } = useLocation()

  useEffect(() => {
    reportGoogleAnalyticsPageView(pathname)
  }, [pathname])

  return null
}
